import React, { useRef, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import avatarImg from '@assets/images/icons/avatar.svg'
import SideNavMenu from '@layout/SideNavMenu'
import NavItem from '@layout/header/NavItem'
import ProfileMenu from '@layout/header/ProfileMenu'
import SearchBar from '@layout/header/SearchBar'
import ScoreService from '@services/ScoreService'
import { useSession } from '@stores/globalData/globalData.store'
import { HELP_CENTER_LINK } from '@utils/global'
import PageRoutes from '@utils/page-routes'

import type { SearchConfig } from '@services/SearchService'

type Props = React.PropsWithChildren<{ searchConfig?: SearchConfig }>

const Header: React.FC<Props> = ({ searchConfig }) => {
  const session = useSession()
  const { t } = useTranslation()

  const outsideClicked = useRef(false)

  const [showLeftNav, setShowLeftNav] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileClose, setShowMobileClose] = useState(false)

  return (
    <>
      <SideNavMenu show={showLeftNav} onHide={() => setShowLeftNav(false)} />
      <ul
        data-testid="header"
        role="menubar"
        className="flex flex-row bg-white dark:bg-black-bg2 border-b border-b-gray dark:border-b-gray-border-dm pl-5 pr-3 relative h-[64px]">
        <li role="menuitem">
          <button
            type="button"
            onClick={() => setShowLeftNav(!showLeftNav)}
            className="flex flex-col justify-center mr-4 h-full">
            <i className="text-xl cursor-pointer nf-icon nf-hamburger text-gray-icons dark:text-gray" />
          </button>
        </li>

        <button
          className="hidden flex-col justify-center mr-2 h-full sm:flex"
          onClick={() => setShowLeftNav(!showLeftNav)}>
          <i
            className={classNames(
              'nf-icon nf-mark nf-duotone text-[44px]',
              session.plan === 'learn' ? 'text-green' : 'text-orange'
            )}
            style={
              {
                '--nf-primary-color': 'white',
                '--nf-secondary-opacity': 1,
              } as React.CSSProperties
            }
          />
        </button>
        {session.isLoggedIn() ? (
          <>
            <NavItem
              onClick={() => ScoreService.createNewScore(session)}
              label={t('palette.create')}
              iconName="nf-create"
            />
            <NavItem
              to={PageRoutes.homePath()}
              label={t('palette.myScores')}
              iconName="nf-myscores"
              external={!session.gatedFeatures.showGatedFeature('myScoresV2', true)}
            />
          </>
        ) : (
          <NavItem exact to="/" external iconName="nf-home" label={t('palette.home')} />
        )}
        {!session.isAnonymousNonPublicCommunity() && (
          <NavItem
            external
            to={PageRoutes.communityPath()}
            iconName="nf-community"
            label={t('palette.community')}
          />
        )}
        {!session.isDefaultCommunity() && session.isSiteAdminOrTeacher() && (
          <NavItem
            to={`/groups#/group/${session.communityGroup?.id}`}
            external
            label={t('palette.manageSite')}
            iconName="nf-manage"
          />
        )}
        <NavItem to={PageRoutes.musicPath()} label={t('palette.music')} iconName="nf-music" />

        <SearchBar searchConfig={searchConfig} onShowResults={setShowMobileClose} />

        <NavItem to={HELP_CENTER_LINK} external label={t('palette.help')} iconName="nf-help" />
        {session.isDefaultCommunity() && !session.isLoggedIn() && (
          <NavItem
            to={PageRoutes.plansPath()}
            label={t('palette.ourPlans')}
            iconName="nf-upgrade"
            iconSize="text-[24px]"
            external
          />
        )}

        {session.isLoggedIn() ? (
          <>
            <button
              type="button"
              className={!showMobileClose ? 'flex' : 'hidden sm:flex'}
              onClick={() => {
                if (!outsideClicked.current) {
                  setShowUserMenu((prevState) => !prevState)
                } else {
                  outsideClicked.current = false
                }
              }}>
              {session.currentUser?.avatar_uri ? (
                <div className="overflow-hidden mx-3 my-3 rounded-full cursor-pointer w-10 h-10">
                  <img alt="Avatar" className="h-10" src={session.currentUser.avatar_uri} />
                </div>
              ) : (
                <img
                  alt="Avatar"
                  src={avatarImg}
                  className="mx-3 my-3 h-10 cursor-pointer"
                  role="image"
                />
              )}
            </button>
            {showUserMenu && (
              <ProfileMenu
                onClose={() => {
                  outsideClicked.current = true
                  setShowUserMenu(false)
                }}
              />
            )}
          </>
        ) : (
          <NavItem
            to="/users/sign_in"
            external
            mobile={!showMobileClose}
            label={t('palette.signIn')}
            iconSize="text-[28px]"
            iconName="nf-avatar"
          />
        )}
      </ul>
    </>
  )
}

export default Header
